/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Participating Company logic module.
 * Handles operations for companies participating in business events
 * including fetching by ID, RFC, and listing all participants.
 * @module logic/participatingCompany
 */

import { refreshToken, signOut } from "../../utils/common.js";

const LOG_NEW_TOKENS = "newTokens: ";

/**
 * Fetches a participating company by event ID and company ID.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {string} companyId - Company identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Participating company data or null on error
 */
export const getParticipatingCompany = async (eventId, companyId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompany/${companyId}`,
      options,
    );

    const result = await response.json();

    logger.info("getParticipatingCompany fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getParticipatingCompany(eventId, companyId, navigate);
    }

    return result;
  } catch (error) {
    logger.error("Error fetching participating company:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Fetches a participating company by event ID and company RFC.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {string} RFC - Company RFC (tax ID)
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Participating company data or null on error
 */
export const getParticipatingCompanyByRFC = async (eventId, RFC, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompany/rfc/${RFC}`,
      options,
    );

    const result = await response.json();

    logger.info("getParticipatingCompanyByRFC fetch result:", { data: result });

    if (result?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getParticipatingCompanyByRFC(eventId, RFC, navigate);
    }

    return result;
  } catch (error) {
    logger.error("Error fetching participating company:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Fetches all participating companies for a business event (filtered by user).
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of participating company objects
 */
export const getParticipatingCompanies = async (eventId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const userId = localStorage.getItem("userId");
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompanies/${userId}?summary=true`,
      options,
    );
    const data = await response.json();
    logger.info("getParticipatingCompanies data: ", { data: data });
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      logger.info(LOG_NEW_TOKENS, { data: newTokens });
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getParticipatingCompanies(eventId, navigate);
    }

    if (Array.isArray(data)) {
      return data;
    } else {
      return [];
    }
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches all participating companies for a business event.
 * @async
 * @param {string} eventId - Business event identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of participating company objects
 */
export const getParticipatingCompaniesFromEvent = async (eventId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/participatingCompanies`,
      options,
    );
    const data = await response.json();
    logger.info("getParticipatingCompanies data: ", { data: data });
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      logger.info(LOG_NEW_TOKENS, { data: newTokens });
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getParticipatingCompaniesFromEvent(eventId, navigate);
    }

    if (Array.isArray(data)) {
      return data;
    } else {
      return [];
    }
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};
